import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Col, Empty, Row, Select, Space, Spin } from 'udesk-ui';
import { Content, Wrapper } from './components';
import Ellipsis from 'src/components/common/Ellipsis';
import { getDataTicketRelatedCallById } from 'src/api/data/ticket/related-call/{id}';
import { getDataByIdDataCallDetail } from 'src/api/data/{id}/dataCallDetail';
import { DataCall, DataCallWrapper } from 'src/api/types';

const transformData = (data?: DataCallWrapper) => {
    return data?.fieldList?.map((f) => {
        return {
            label: f.label,
            value: data.fieldValueMap?.[f.id!],
        };
    });
};

interface TemplateProps {
    style?: React.CSSProperties;
    height?: number;
    callId?: number;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { style, height, callId } = props;

    const contentHeight = useMemo(() => {
        return height && height >= 38 ? height - 38 : undefined;
    }, [height]);

    const options = useMemo(
        () => [
            {
                value: Udesk.enums.inspectDataSources.voiceCall.id,
                label: /* 通话 */ UdeskLocales['current'].components.dialogInformation.index.call,
            },
            {
                value: Udesk.enums.inspectDataSources.textDialogue.id,
                label: /* 对话 */ UdeskLocales['current'].components.dialogInformation.index
                    .dialogue,
            },
        ],
        []
    );

    const [source, setSource] = React.useState<number>(Udesk.enums.inspectDataSources.voiceCall.id);

    const [currentId, setCurrentId] = React.useState<number>();

    const showFirst = (data?: DataCall[], source?: number) => {
        const c = data?.filter((d) => d.inspectDataSource === source)?.[0];
        setCurrentId(c?.id);
        return !!c;
    };

    const { data, loading } = useRequest(
        () =>
            getDataTicketRelatedCallById({
                segments: {
                    id: callId!,
                },
            }),
        {
            ready: !!callId,
            refreshDeps: [callId],
            onSuccess: (res) => {
                // 这里，遍历所有类型，直到找到有数据的类型
                for (let i = 0; i < options.length; i++) {
                    setSource(options[i].value);
                    const isDone = showFirst(res.data, options[i].value);
                    if (isDone) {
                        break;
                    } else {
                        // 如果都没有数据，则使用第一个类型
                        if (i === options.length - 1) {
                            setSource(options[0]?.value);
                        }
                    }
                }
            },
        }
    );

    const onSourceChange = (value: number) => {
        setSource(value);
        showFirst(data?.data, value);
    };

    const { data: dialogDetail, loading: dialogDetailLoading } = useRequest(
        () =>
            getDataByIdDataCallDetail({
                segments: {
                    id: currentId!,
                },
            }),
        {
            ready: !!currentId,
            refreshDeps: [currentId],
        }
    );

    const dialogDetailFields = transformData(dialogDetail?.data);

    return (
        <Wrapper style={{ height, ...style }}>
            <Spin spinning={loading || dialogDetailLoading}>
                <div>
                    <Space wrap>
                        <Select
                            style={{ width: 80 }}
                            options={options}
                            value={source}
                            onChange={onSourceChange}
                        />

                        <Select
                            style={{ width: 140 }}
                            options={data?.data
                                ?.filter((d) => d.inspectDataSource === source)
                                .map((d) => {
                                    return {
                                        label: d.id,
                                        value: d.id!,
                                    };
                                })}
                            value={currentId}
                            onChange={setCurrentId}
                        />
                    </Space>
                </div>

                {currentId && dialogDetailFields?.length ? (
                    <Content height={contentHeight}>
                        {dialogDetailFields.map((content, idx) => (
                            <Row key={idx} style={{ marginBottom: 6 }} gutter={4} wrap={false}>
                                <Col flex="120px">
                                    <Ellipsis
                                        style={{ width: '100%', textAlign: 'right' }}
                                        text={content.label}
                                        suffix="："
                                    />
                                </Col>

                                <Col flex="auto">
                                    <Ellipsis text={content.value as string} />
                                </Col>
                            </Row>
                        ))}
                    </Content>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Spin>
        </Wrapper>
    );
});

export default Template;
